import {
  useQuery,
  useQueryClient,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  UseMutateFunction,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import UserGeneratedContentService from '../../services/user-generated-content';
import {
  UserGeneratedContentResponse,
  UserGeneratedContent,
} from '../../models/user-generated-contents/types';

export const USER_GENERATED_CONTENTS_QUERY_KEY = ['UserGeneratedContent'];
export const USER_GENERATED_CONTENTS_LIST_QUERY_KEY = [
  'UserGeneratedContentsList',
];

// Don't retry on 4xx — the API has answered (403 for non-owner, 404 for missing).
// Retrying them just delays the locked view by ~7s with the default retry: 3 + backoff.
// 5xx / network errors keep the default retry behavior.
export const shouldRetryUgcQuery = (
  failureCount: number,
  error: unknown
): boolean => {
  const status = (error as AxiosError | undefined)?.response?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return failureCount < 3;
};

type UserGeneratedContentMutationReturnType<T = UserGeneratedContent> = {
  isWorking: boolean;
  mutate: UseMutateFunction<UserGeneratedContentResponse, Error, T>;
};

export type QueryResponse<T> = {
  isLoading: boolean;
  errorMessage?: string;
  error?: Error | null;
  data?: T;
  isSuccess?: boolean;
  invalidateQuery?: () => void;
  isFetching?: boolean | undefined;
};

export const useUserGeneratedContentQuery = (
  id: number | undefined,
  options?: Omit<
    UseQueryOptions<
      UserGeneratedContentResponse,
      Error,
      UserGeneratedContentResponse,
      string[]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    UserGeneratedContentResponse,
    Error,
    UserGeneratedContentResponse,
    string[]
  >({
    queryKey: [...USER_GENERATED_CONTENTS_QUERY_KEY, String(id)],
    queryFn: async () => {
      if (!id) {
        throw new Error('An ID is required');
      }

      const service = new UserGeneratedContentService(id);
      const response = await service.fetch();

      return response.data;
    },
    enabled: !!id,
    retry: shouldRetryUgcQuery,
    ...options,
  });
};

export const useCreateUserGeneratedContentMutation = (
  options?: Omit<
    UseMutationOptions<
      UserGeneratedContentResponse,
      Error,
      UserGeneratedContent
    >,
    'mutationFn'
  >
): UserGeneratedContentMutationReturnType => {
  const queryClient = useQueryClient();
  const { isLoading, mutate } = useMutation<
    UserGeneratedContentResponse,
    Error,
    UserGeneratedContent
  >({
    mutationFn: async (data: UserGeneratedContent) => {
      const service = new UserGeneratedContentService(undefined);
      const response = await service.create(data);
      return response.data;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([
        ...USER_GENERATED_CONTENTS_QUERY_KEY,
        String(data.data.attributes.id),
      ]);
      options?.onSuccess?.(data, variables, context);
    },
  });

  return { isWorking: isLoading, mutate };
};

export const useUpdateUserGeneratedContentMutation = (
  id?: number,
  options?: Omit<
    UseMutationOptions<
      UserGeneratedContentResponse,
      Error,
      Partial<UserGeneratedContent>
    >,
    'mutationFn'
  >
): UserGeneratedContentMutationReturnType<Partial<UserGeneratedContent>> => {
  const queryClient = useQueryClient();
  const { isLoading, mutate } = useMutation<
    UserGeneratedContentResponse,
    Error,
    Partial<UserGeneratedContent>
  >({
    mutationFn: async (data: Partial<UserGeneratedContent>) => {
      if (!id) throw new Error('An ID is required');

      const service = new UserGeneratedContentService(id);
      const response = await service.update(data);
      return response.data;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(
        [...USER_GENERATED_CONTENTS_QUERY_KEY, String(id)],
        data
      );
      options?.onSuccess?.(data, variables, context);
    },
  });
  return { isWorking: isLoading, mutate };
};

export const useContentSubmissionMutation = (
  options?: Omit<
    UseMutationOptions<
      UserGeneratedContentResponse,
      Error,
      UserGeneratedContent
    >,
    'mutationFn'
  >
): UserGeneratedContentMutationReturnType => {
  const { isLoading, mutate } = useMutation<
    UserGeneratedContentResponse,
    Error,
    UserGeneratedContent
  >({
    mutationFn: async (data: UserGeneratedContent) => {
      if (!data.id) throw new Error('An ID is required');

      const service = new UserGeneratedContentService(data.id);
      const response = await service.submit();
      return response.data;
    },
    ...options,
  });
  return { isWorking: isLoading, mutate };
};
